import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, Outlet, useLocation, useMatches } from "react-router";

import { AppShell } from "@/components/layout/AppShell";
import { Loading, Skeleton } from "@/components/ui/Skeleton";
import { useSession } from "@/features/auth/useSession";
import { ClaraSetup } from "@/features/onboarding/ClaraSetup";
import { type BusinessProfile, IndustrySetup } from "@/features/onboarding/IndustrySetup";
import { UnauthenticatedError, apiFetch } from "@/lib/api/client";
import { getNavigation } from "@/lib/api/endpoints";
import { keys } from "@/lib/api/keys";

/**
 * El guard de sesión y el shell.
 *
 * ## Lo que se perdió al salir de Next, y cómo se compensa
 *
 * El layout anterior resolvía la sesión **en el servidor**: si no había, hacía
 * `redirect("/login")` y el navegador nunca recibía una sola línea de la
 * aplicación. Acá eso no existe — el shell ya está descargado y corriendo
 * cuando recién vamos a preguntar quién sos.
 *
 * La compensación es no mostrar nada de la aplicación mientras no se sepa. No
 * es una decisión estética: pintar la barra lateral con los módulos y sacarla
 * medio segundo después deja ver, aunque sea un instante, la forma de una
 * pantalla a la que quizá esta persona no tiene acceso.
 *
 * Las dos consultas van juntas —`useQuery` en paralelo, no encadenadas— igual
 * que el `Promise.all` que tenía el layout de Next. Encadenarlas duplicaría la
 * espera, que a esta latencia se nota.
 */
export function AppLayout() {
  const session = useSession();
  /* Que la bienvenida se dé por terminada **en esta pantalla**, sin esperar a
     que vuelva el perfil del servidor. Sin esto, apretar «entrar al tablero»
     deja la conversación puesta hasta que refresque la consulta, y parece que
     el botón no hizo nada. */
  const [saltada, setSaltada] = useState(false);
  /* El respaldo de la bienvenida: elegir el rubro de una lista.
     Clara casi siempre lo deduce de lo que le cuentan, pero si alguien no
     quiere charlar tiene que poder decirlo igual — sin el rubro, la aplicación
     no sabe si mostrar Propiedades. */
  const [aMano, setAMano] = useState(false);
  const navigation = useQuery({
    queryKey: keys.navigation,
    queryFn: getNavigation,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const profile = useQuery({
    queryKey: keys.profile,
    queryFn: () => apiFetch<BusinessProfile>("/api/v1/me/profile"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const location = useLocation();
  // Una pantalla rediseñada declara en su ruta que el padding es suyo.
  const ownsPadding = useMatches().some((match) => hasOwnPadding(match.handle));

  const sinSesion =
    session.error instanceof UnauthenticatedError ||
    navigation.error instanceof UnauthenticatedError;

  if (sinSesion) {
    // `state` guarda a dónde quería ir, para volver ahí después de entrar.
    return (
      <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    );
  }

  if (session.isPending || navigation.isPending || profile.isPending) {
    return <ShellSkeleton />;
  }

  /* La bienvenida, antes que cualquier otra cosa.
   *
   * Aparece **solo cuando no hay nada que deducir**: sin rubro elegido y sin
   * datos —ni CRM inmobiliario conectado ni catálogo— que permitan adivinarlo.
   * Un espacio que ya tiene datos nunca la ve.
   *
   * Solo la ve quien manda: define lo que ve todo el equipo. A un agente que
   * entra primero se le muestra la aplicación genérica, no una configuración
   * que no le corresponde.
   *
   * Se puede saltear, y el botón está a la vista. Un alta obligatoria es una
   * pared: quien vino a mirar el tablero tiene que poder entrar. */
  if (
    profile.data?.needs_setup &&
    // Quien ya la dio por terminada no la vuelve a ver, aunque Clara no haya
    // llegado a deducir el rubro. Sin esto, saltearla no sirve de nada: la
    // pantalla reaparece en cada login, para siempre.
    !profile.data.onboarding_completed &&
    !saltada &&
    (session.data?.role === "owner" || session.data?.role === "admin")
  ) {
    return aMano ? (
      <IndustrySetup tenantName={session.data.tenant_name} />
    ) : (
      <ClaraSetup onSkip={() => setSaltada(true)} onManual={() => setAMano(true)} />
    );
  }

  if (session.error || navigation.error || !session.data || !navigation.data) {
    return <ShellError message={session.error?.message ?? navigation.error?.message} />;
  }

  return (
    <AppShell user={session.data} sections={navigation.data.sections} padded={!ownsPadding}>
      <Outlet />
    </AppShell>
  );
}

/**
 * El esqueleto del shell mismo.
 *
 * Dibuja la barra oscura con sus medidas reales. Si acá se usara
 * un bloque gris genérico, al llegar los datos saltaría toda la pantalla, que
 * es peor que la espera.
 */
/** `handle: { ownsPadding: true }` en la ruta: la pantalla pone su propio padding. */
function hasOwnPadding(handle: unknown): boolean {
  return (
    typeof handle === "object" &&
    handle !== null &&
    (handle as { ownsPadding?: unknown }).ownsPadding === true
  );
}

function ShellSkeleton() {
  return (
    <Loading label="Cargando Clara…">
      <div className="bg-background flex h-screen overflow-hidden">
        <aside className="bg-rail hidden h-full w-[252px] flex-col lg:flex">
          <div className="border-rail-border flex h-16 items-center gap-2.5 border-b px-4">
            <div className="bg-rail-hover size-9 shrink-0 animate-pulse rounded-full" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="bg-rail-hover h-3.5 w-16 animate-pulse rounded-md" />
              <div className="bg-rail-hover h-3 w-24 animate-pulse rounded-md" />
            </div>
          </div>
          <div className="space-y-1.5 p-2.5 pt-4">
            {Array.from({ length: 9 }, (_, index) => (
              <div
                key={index}
                className="bg-rail-hover rounded-chip h-10 w-full animate-pulse"
              />
            ))}
          </div>
        </aside>

        <div className="flex-1 space-y-2 p-6">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-80" />
        </div>
      </div>
    </Loading>
  );
}

function ShellError({ message }: { message?: string | undefined }) {
  return (
    <main className="grid min-h-screen place-items-center p-6">
      <div className="rounded-card border-border bg-surface max-w-sm border p-6 text-center">
        <h1 className="font-semibold">No se pudo cargar Clara</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {message ?? "La API no respondió."}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-chip mt-4 px-4 py-2 text-sm font-medium"
        >
          Reintentar
        </button>
      </div>
    </main>
  );
}
